// ./src/contexts/ActiveTravelContext/ActiveTravelContext.tsx
//
// Standalone context for tracking the user's currently active (in-progress) travel.
// Lives at the layout level (wraps entire dashboard) so every page can read/write
// active travel state without prop drilling.
//
// Architecture:
// - Self-contained: calls TravelService and ExpenseService directly
// - Two-step loading: list() finds the active travel, then get() fetches full
//   record including waypoints. Background refreshes skip get() when the travel
//   ID hasn't changed (waypoints are already loaded).
// - Auto-polls: configurable refresh interval with visibility-aware pausing
// - Pattern-consistent: ApiError for errors, OpResult for operation returns,
//   ref-based stability to prevent unnecessary re-renders and effect re-fires
// - Race-condition safe: monotonic call IDs discard stale load responses
// - addWaypoint: creates a travel point expense and appends it to the local
//   waypoints array + updates lastRecord, avoiding an extra get() call

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { TRAVEL_STATUSES, EXPENSE_TYPES } from "@/config";

import { ApiError } from "@/utils/apiResultHandler";

import {
  Travel,
  TravelInput,
  TravelService,
  TrackingStatusValue,
  TRACKING_STATUS
} from "@/utils/api/TravelService";
import { TravelTrackingService } from "@/utils/api/TravelTrackingService";
import {
  Expense,
  ExpenseInput,
  ExpenseService,
} from "@/utils/api/ExpenseService";

import { useApp } from "@/contexts/AppContext";

import { TrackingSessionState_UI, useTrackingSession } from "@/hooks/useTrackingSession";

// ============================================
// Constants
// ============================================

/**
 * Default refresh interval in milliseconds (60 seconds).
 * Can be overridden via VITE_ACTIVE_TRAVEL_REFRESH_INTERVAL env var.
 */
const DEFAULT_REFRESH_INTERVAL_MS = 60 * 1000;

/**
 * Get refresh interval from environment or use default.
 */
const getDefaultRefreshInterval = (): number => {
  const envValue = import.meta.env.VITE_ACTIVE_TRAVEL_REFRESH_INTERVAL;
  if (envValue) {
    const parsed = parseInt(envValue, 10);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return DEFAULT_REFRESH_INTERVAL_MS;
};

// ============================================
// Types
// ============================================

export interface ActiveTravelContextValue {
  // State
  activeTravel: Travel | null;
  loading: boolean;

  // Computed
  hasActiveTravel: boolean;
  activeTravelCarId: string | null;
  /** The most recent waypoint (last element of activeTravel.waypoints) */
  latestWaypoint: Expense | null;

  /** Whether live GPS tracking is currently active */
  isTracking: boolean;
  /** Whether live GPS tracking is paused */
  isTrackingPaused: boolean;
  /** Whether any tracking has been started (active, paused, or completed) */
  hasTracking: boolean;
  /** Current tracking status value from the travel record */
  trackingStatus: TrackingStatusValue;
  /** Current segment ID (incremented on each resume) */
  currentSegmentId: number;
  /** GPS-derived distance in km */
  gpsDistance: number;

  // Operations (return OpResult for consistency with other hooks)
  startTravel: (params: TravelInput) => Promise<OpResult>;
  completeTravel: (travelId: string, params: TravelInput) => Promise<OpResult>;
  addWaypoint: (params: ExpenseInput) => Promise<OpResult>;

  pauseTracking: () => Promise<OpResult>;
  resumeTracking: () => Promise<OpResult>;
  discardTracking: () => Promise<OpResult>;

  // Operation states
  isStarting: boolean;
  isCompleting: boolean;
  isAddingWaypoint: boolean;

  isPausingTracking: boolean;
  isResumingTracking: boolean;
  isDiscardingTracking: boolean;

  // Live GPS tracking session (client-side engine)
  /** The full client-side tracking session state (speed, duration, accuracy, etc.) */
  trackingSession: TrackingSessionState_UI;

  // Refresh/reload
  refresh: () => Promise<void>;

  // Manual state update (for when travel is updated elsewhere)
  setActiveTravel: (travel: Travel | null) => void;

  // Clear active travel (when completed or deleted)
  clearActiveTravel: () => void;

  // Error handling (ApiError for field-level support)
  lastError: ApiError | null;
  clearError: () => void;

  // Refresh interval info
  refreshIntervalMs: number;
  isRefreshPaused: boolean;
}

export interface ActiveTravelProviderProps {
  children: React.ReactNode;

  /** Whether to auto-load active travel on mount (default: true) */
  autoLoad?: boolean;

  /**
   * Refresh interval in milliseconds (default: 60000 = 1 minute).
   * Set to 0 or null to disable auto-refresh.
   * Can also be set via VITE_ACTIVE_TRAVEL_REFRESH_INTERVAL env var.
   */
  refreshIntervalMs?: number | null;

  /**
   * Whether to pause refresh when tab/window is not visible (default: true).
   * This saves resources when user is not actively viewing the app.
   */
  pauseWhenHidden?: boolean;

  /**
   * Whether to refresh immediately when tab becomes visible again (default: true).
   * Only applies when pauseWhenHidden is true.
   */
  refreshOnVisible?: boolean;
}

// ============================================
// Context
// ============================================

const ActiveTravelContext = createContext<ActiveTravelContextValue | undefined>(
  undefined
);

// ============================================
// Provider
// ============================================

export const ActiveTravelProvider: React.FC<ActiveTravelProviderProps> = ({
  children,
  autoLoad = true,
  refreshIntervalMs: propRefreshInterval,
  pauseWhenHidden = true,
  refreshOnVisible = true,
}) => {
  const { user, isAuthenticated } = useApp();

  // Determine refresh interval (prop > env > default)
  const refreshIntervalMs = useMemo(() => {
    if (propRefreshInterval === null || propRefreshInterval === 0) {
      return 0; // Disabled
    }
    if (propRefreshInterval !== undefined && propRefreshInterval > 0) {
      return propRefreshInterval;
    }
    return getDefaultRefreshInterval();
  }, [propRefreshInterval]);

  // ============================================
  // State
  // ============================================

  const [activeTravel, setActiveTravelState] = useState<Travel | null>(null);
  const [loading, setLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isAddingWaypoint, setIsAddingWaypoint] = useState(false);
  const [lastError, setLastError] = useState<ApiError | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

  const [isPausingTracking, setIsPausingTracking] = useState(false);
  const [isResumingTracking, setIsResumingTracking] = useState(false);
  const [isDiscardingTracking, setIsDiscardingTracking] = useState(false);

  // ============================================
  // Refs for stability & race-condition protection
  // ============================================

  // Interval management
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastRefreshRef = useRef<number>(0);

  // Monotonic call ID for race-condition protection on load
  const loadCallIdRef = useRef(0);

  // Track the ID of the currently loaded travel with waypoints.
  // On background refreshes, if the travel ID matches we skip the get() call
  // because waypoints are already loaded.
  const loadedWithWaypointsTravelIdRef = useRef<string | null>(null);

  // Unmount guard
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Stable refs for values read inside callbacks — prevents callback recreation
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  const userIdRef = useRef(user?.id);
  userIdRef.current = user?.id;

  const activeTravelRef = useRef(activeTravel);
  activeTravelRef.current = activeTravel;

  const hasLoadedRef = useRef(hasLoaded);
  hasLoadedRef.current = hasLoaded;

  // ============================================
  // Computed values
  // ============================================

  const hasActiveTravel = activeTravel !== null;

  const activeTravelCarId = useMemo(
    () => activeTravel?.carId || activeTravel?.car?.id || null,
    [activeTravel]
  );

  const isRefreshPaused = useMemo(
    () => pauseWhenHidden && !isTabVisible,
    [pauseWhenHidden, isTabVisible]
  );

  /** The most recent waypoint from the active travel's waypoints array */
  const latestWaypoint = useMemo(() => {
    const waypoints = activeTravel?.waypoints;
    if (!waypoints || waypoints.length === 0) return null;
    return waypoints[waypoints.length - 1];
  }, [activeTravel?.waypoints]);

  const trackingStatus: TrackingStatusValue = useMemo(
    () => activeTravel?.trackingStatus ?? TRACKING_STATUS.NONE,
    [activeTravel?.trackingStatus]
  );

  const isTracking = trackingStatus === TRACKING_STATUS.ACTIVE;
  const isTrackingPaused = trackingStatus === TRACKING_STATUS.PAUSED;
  const hasTracking = trackingStatus !== TRACKING_STATUS.NONE;

  const currentSegmentId = useMemo(
    () => activeTravel?.currentSegmentId ?? 0,
    [activeTravel?.currentSegmentId]
  );

  const gpsDistance = useMemo(
    () => activeTravel?.gpsDistance ?? 0,
    [activeTravel?.gpsDistance]
  );

  // ============================================
  // Client-side GPS tracking session
  // ============================================

  const { session: trackingSession, actions: trackingActions } = useTrackingSession();

  // ============================================
  // Load active travel (two-step: list → get)
  // ============================================

  const loadActiveTravel = useCallback(
    async (isBackgroundRefresh = false): Promise<void> => {
      if (!isAuthenticatedRef.current || !userIdRef.current) {
        setActiveTravelState(null);
        loadedWithWaypointsTravelIdRef.current = null;
        return;
      }

      // Monotonic call ID — discard stale responses
      const callId = ++loadCallIdRef.current;

      // Only show loading indicator for initial load, not background refreshes
      if (!isBackgroundRefresh) {
        setLoading(true);
      }

      try {
        // Step 1: Query for in-progress travels, get the most recent one
        const listResult = await TravelService.list(
          {
            status: [TRAVEL_STATUSES.IN_PROGRESS],
            userId: userIdRef.current ? [userIdRef.current] : undefined,
          },
          { page: 1, pageSize: 1 },
          [{ name: "firstDttm", order: "DESC" }]
        );

        // Stale response guard
        if (callId !== loadCallIdRef.current || !mountedRef.current) return;

        if (listResult.didFail()) {
          if (!isBackgroundRefresh) {
            setActiveTravelState(null);
            loadedWithWaypointsTravelIdRef.current = null;
          }
          setLastError(
            ApiError.fromResult(listResult, "Failed to load active travel")
          );
          lastRefreshRef.current = Date.now();
          return;
        }

        const travels = listResult.didSucceedAndHasData()
          ? listResult.getData()
          : [];

        if (travels.length === 0) {
          // No active travel
          setActiveTravelState(null);
          setLastError(null);
          loadedWithWaypointsTravelIdRef.current = null;
          lastRefreshRef.current = Date.now();
          return;
        }

        const foundTravel = travels[0] as Travel;
        const foundId = foundTravel.id;

        // Step 2: Fetch full record with waypoints via get().
        // Skip if this is a background refresh and we already have waypoints
        // for the same travel ID.
        const alreadyHasWaypoints =
          isBackgroundRefresh &&
          foundId &&
          loadedWithWaypointsTravelIdRef.current === foundId;

        if (alreadyHasWaypoints) {
          // Travel ID unchanged — update core fields from list result
          // but preserve existing waypoints and richer get() data
          setActiveTravelState((prev) => {
            if (!prev || prev.id !== foundId) return foundTravel;
            return {
              ...foundTravel,
              waypoints: prev.waypoints,
              userCreated: prev.userCreated,
              userUpdated: prev.userUpdated,
              account: prev.account,
            };
          });
          setLastError(null);
          lastRefreshRef.current = Date.now();
          return;
        }

        // Need to fetch full record with waypoints
        if (!foundId) {
          // Shouldn't happen, but guard
          setActiveTravelState(foundTravel);
          setLastError(null);
          lastRefreshRef.current = Date.now();
          return;
        }

        const getResult = await TravelService.get(foundId);

        // Stale response guard (again, after second async call)
        if (callId !== loadCallIdRef.current || !mountedRef.current) return;

        if (getResult.code === OP_RESULT_CODES.OK && getResult.data) {
          const fullTravel = (getResult.data as Travel[])[0];
          setActiveTravelState(fullTravel || foundTravel);
          loadedWithWaypointsTravelIdRef.current = foundId;
          setLastError(null);
        } else {
          // get() failed — still use the list result (without waypoints)
          setActiveTravelState(foundTravel);
          loadedWithWaypointsTravelIdRef.current = null;
          // Don't set error — the travel itself loaded fine, just waypoints missing
        }

        lastRefreshRef.current = Date.now();
      } catch (error) {
        // Stale response guard
        if (callId !== loadCallIdRef.current || !mountedRef.current) return;

        if (!isBackgroundRefresh) {
          setActiveTravelState(null);
          loadedWithWaypointsTravelIdRef.current = null;
        }
        setLastError(
          ApiError.fromException(error, "Failed to load active travel")
        );
      } finally {
        if (callId === loadCallIdRef.current && mountedRef.current) {
          if (!isBackgroundRefresh) {
            setLoading(false);
          }
          setHasLoaded(true);
        }
      }
    },
    [] // Stable: reads everything from refs
  );

  // ============================================
  // Start a new travel
  // ============================================

  const startTravel = useCallback(
    async (params: TravelInput): Promise<OpResult> => {
      if (!isAuthenticatedRef.current) {
        const result = OpResult.fail(
          OP_RESULT_CODES.UNAUTHORIZED,
          {},
          "Not authenticated"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      // Prevent starting if there's already an active travel
      if (activeTravelRef.current) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "A travel is already in progress"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsStarting(true);
      setLastError(null);

      try {
        const result = await TravelService.create(params);

        if (!mountedRef.current) return result;

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newTravel = (result.data as Travel[])[0];
          // Initialize waypoints from firstRecord (the starting point)
          // so latestWaypoint is available for odometer pre-population
          if (newTravel) {
            newTravel.waypoints = newTravel.firstRecord
              ? [newTravel.firstRecord]
              : [];
          }
          setActiveTravelState(newTravel);

          // If tracking was enabled, start the client-side GPS session
          if (
            newTravel?.trackingStatus === TRACKING_STATUS.ACTIVE &&
            newTravel?.id
          ) {
            trackingActions.start(newTravel.id, newTravel.currentSegmentId ?? 0);
          }

          loadedWithWaypointsTravelIdRef.current = newTravel?.id || null;
          setLastError(null);
          return result;
        }

        setLastError(
          ApiError.fromResult(result, "Failed to start travel")
        );
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        if (mountedRef.current) {
          setLastError(
            ApiError.fromException(error, "Failed to start travel")
          );
        }
        return result;
      } finally {
        if (mountedRef.current) {
          setIsStarting(false);
        }
      }
    },
    [trackingActions] // Stable: reads activeTravel and isAuthenticated from refs
  );

  // ============================================
  // Complete (stop) an active travel
  // ============================================

  const completeTravel = useCallback(
    async (travelId: string, params: TravelInput): Promise<OpResult> => {
      if (!isAuthenticatedRef.current) {
        const result = OpResult.fail(
          OP_RESULT_CODES.UNAUTHORIZED,
          {},
          "Not authenticated"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      if (!travelId) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Travel ID is required for completing"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsCompleting(true);
      setLastError(null);

      try {
        // Stop client-side tracking session first (flushes buffer)
        const currentStatus = activeTravelRef.current?.trackingStatus;
        if (
          activeTravelRef.current?.id === travelId &&
          (currentStatus === TRACKING_STATUS.ACTIVE || currentStatus === TRACKING_STATUS.PAUSED)
        ) {
          await trackingActions.stop();
        }

        const result = await TravelService.update({ id: travelId }, params);

        if (!mountedRef.current) return result;

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          // Clear active travel if we just completed it
          if (activeTravelRef.current?.id === travelId) {
            setActiveTravelState(null);
            loadedWithWaypointsTravelIdRef.current = null;
          }
          setLastError(null);
          return result;
        }

        setLastError(
          ApiError.fromResult(result, "Failed to complete travel")
        );
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        if (mountedRef.current) {
          setLastError(
            ApiError.fromException(error, "Failed to complete travel")
          );
        }
        return result;
      } finally {
        if (mountedRef.current) {
          setIsCompleting(false);
        }
      }
    },
    [trackingActions] // Stable: reads activeTravel from ref
  );

  // ============================================
  // Add waypoint to the active travel
  // ============================================

  const addWaypoint = useCallback(
    async (params: ExpenseInput): Promise<OpResult> => {
      if (!isAuthenticatedRef.current) {
        const result = OpResult.fail(
          OP_RESULT_CODES.UNAUTHORIZED,
          {},
          "Not authenticated"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      const currentTravel = activeTravelRef.current;

      if (!currentTravel?.id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "No active travel to add waypoint to"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      // Ensure the expense is linked to the active travel
      const waypointParams: ExpenseInput = {
        ...params,
        expenseType: EXPENSE_TYPES.TRAVEL_POINT,
        travelId: currentTravel.id,
        carId: params.carId || currentTravel.carId,
      };

      setIsAddingWaypoint(true);
      setLastError(null);

      try {
        // Pause client-side GPS + flush buffer if tracking was active
        const wasTracking =
          activeTravelRef.current?.trackingStatus === TRACKING_STATUS.ACTIVE;

        if (wasTracking) {
          await trackingActions.pause();

          // Optimistically mark as paused in local state + ref immediately,
          // so resumeTracking doesn't see a stale ACTIVE status while the
          // fire-and-forget TravelService.pause() is still in-flight.
          setActiveTravelState((prev) => {
            if (!prev || prev.id !== currentTravel.id) return prev;
            const merged = { ...prev, trackingStatus: TRACKING_STATUS.PAUSED };
            activeTravelRef.current = merged;
            return merged;
          });
        }

        const result = await ExpenseService.create(waypointParams);

        if (!mountedRef.current) return result;

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newWaypoint = (result.data as Expense[])[0];

          // Pause on server so trackingStatus reflects paused state.
          // Fire-and-forget — don't block the waypoint success on this.
          if (wasTracking && currentTravel.id) {
            TravelService.pause(currentTravel.id).then((pauseResult) => {
              if (
                pauseResult.code === OP_RESULT_CODES.OK &&
                pauseResult.data &&
                mountedRef.current
              ) {
                const updatedTravel = (pauseResult.data as Travel[])[0];
                setActiveTravelState((prev) => {
                  if (!prev || prev.id !== currentTravel.id) return prev;
                  return { ...prev, ...updatedTravel, waypoints: prev.waypoints };
                });
              }
            });
          }

          if (newWaypoint) {
            // Optimistic update: append waypoint to local state
            // and update lastRecord so subsequent drawers get fresh data
            setActiveTravelState((prev) => {
              if (!prev || prev.id !== currentTravel.id) return prev;

              const updatedWaypoints = [
                ...(prev.waypoints || []),
                newWaypoint,
              ];

              return {
                ...prev,
                waypoints: updatedWaypoints,
                // Update lastRecord to the new waypoint
                lastRecord: newWaypoint,
                lastRecordId: newWaypoint.id,
                // Update lastOdometer if waypoint has odometer
                lastOdometer:
                  newWaypoint.odometer != null
                    ? newWaypoint.odometer
                    : prev.lastOdometer,
                // Update lastDttm if waypoint has whenDone
                lastDttm: newWaypoint.whenDone || prev.lastDttm,
              };
            });
          }

          setLastError(null);
          return result;
        }

        setLastError(
          ApiError.fromResult(result, "Failed to add waypoint")
        );
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        if (mountedRef.current) {
          setLastError(
            ApiError.fromException(error, "Failed to add waypoint")
          );
        }
        return result;
      } finally {
        if (mountedRef.current) {
          setIsAddingWaypoint(false);
        }
      }
    },
    [trackingActions]
  );

  const pauseTracking = useCallback(async (): Promise<OpResult> => {
    const currentTravel = activeTravelRef.current;

    if (!currentTravel?.id) {
      const result = OpResult.fail(
        OP_RESULT_CODES.VALIDATION_FAILED,
        {},
        "No active travel to pause tracking for"
      );
      setLastError(ApiError.fromResult(result));
      return result;
    }

    if (currentTravel.trackingStatus !== TRACKING_STATUS.ACTIVE) {
      const result = OpResult.fail(
        OP_RESULT_CODES.VALIDATION_FAILED,
        {},
        "Tracking is not active"
      );
      setLastError(ApiError.fromResult(result));
      return result;
    }

    setIsPausingTracking(true);
    setLastError(null);

    try {
      // Pause client-side GPS session first (flushes buffer, stops GPS)
      await trackingActions.pause();

      const result = await TravelService.pause(currentTravel.id);

      if (!mountedRef.current) return result;

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const updatedTravel = (result.data as Travel[])[0];

        // Merge updated tracking fields into existing travel state
        setActiveTravelState((prev) => {
          if (!prev || prev.id !== currentTravel.id) return prev;
          const merged = { ...prev, ...updatedTravel, waypoints: prev.waypoints };
          // Sync ref immediately so resumeTracking can read the updated status
          // without waiting for the next render cycle
          activeTravelRef.current = merged;
          return merged;
        });

        setLastError(null);
        return result;
      }

      setLastError(
        ApiError.fromResult(result, "Failed to pause tracking")
      );
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      if (mountedRef.current) {
        setLastError(
          ApiError.fromException(error, "Failed to pause tracking")
        );
      }
      return result;
    } finally {
      if (mountedRef.current) {
        setIsPausingTracking(false);
      }
    }
  }, [trackingActions]); // Stable: reads activeTravel from ref

  // ============================================
  // +++ TRACKING: Resume tracking
  // ============================================

  const resumeTracking = useCallback(async (): Promise<OpResult> => {
    const currentTravel = activeTravelRef.current;

    if (!currentTravel?.id) {
      const result = OpResult.fail(
        OP_RESULT_CODES.VALIDATION_FAILED,
        {},
        "No active travel to resume tracking for"
      );
      setLastError(ApiError.fromResult(result));
      return result;
    }

    if (currentTravel.trackingStatus !== TRACKING_STATUS.PAUSED) {
      const result = OpResult.fail(
        OP_RESULT_CODES.VALIDATION_FAILED,
        {},
        "Tracking is not paused"
      );
      setLastError(ApiError.fromResult(result));
      return result;
    }

    setIsResumingTracking(true);
    setLastError(null);

    try {
      const result = await TravelService.resume(currentTravel.id);

      if (!mountedRef.current) return result;

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const updatedTravel = (result.data as Travel[])[0];

        // Merge updated tracking fields, preserving waypoints
        setActiveTravelState((prev) => {
          if (!prev || prev.id !== currentTravel.id) return prev;
          const merged = { ...prev, ...updatedTravel, waypoints: prev.waypoints };
          activeTravelRef.current = merged;
          return merged;
        });

        // Resume client-side GPS session with new segment ID from server
        const newSegmentId = updatedTravel?.currentSegmentId ?? 0;
        await trackingActions.resume(newSegmentId);

        setLastError(null);
        return result;
      }

      setLastError(
        ApiError.fromResult(result, "Failed to resume tracking")
      );
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      if (mountedRef.current) {
        setLastError(
          ApiError.fromException(error, "Failed to resume tracking")
        );
      }
      return result;
    } finally {
      if (mountedRef.current) {
        setIsResumingTracking(false);
      }
    }
  }, [trackingActions]); // Stable: reads activeTravel from ref

  // ============================================
  // +++ TRACKING: Discard tracking
  // ============================================

  const discardTracking = useCallback(async (): Promise<OpResult> => {
    const currentTravel = activeTravelRef.current;

    if (!currentTravel?.id) {
      const result = OpResult.fail(
        OP_RESULT_CODES.VALIDATION_FAILED,
        {},
        "No active travel to discard tracking for"
      );
      setLastError(ApiError.fromResult(result));
      return result;
    }

    if (
      currentTravel.trackingStatus === TRACKING_STATUS.NONE ||
      currentTravel.trackingStatus == null
    ) {
      const result = OpResult.fail(
        OP_RESULT_CODES.VALIDATION_FAILED,
        {},
        "No tracking data to discard"
      );
      setLastError(ApiError.fromResult(result));
      return result;
    }

    setIsDiscardingTracking(true);
    setLastError(null);

    try {
      // Stop client-side GPS session and clear local storage
      await trackingActions.discard();

      const result = await TravelTrackingService.discard(currentTravel.id);

      if (!mountedRef.current) return result;

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const updatedTravel = (result.data as Travel[])[0];

        // Merge reset tracking fields, preserving waypoints
        setActiveTravelState((prev) => {
          if (!prev || prev.id !== currentTravel.id) return prev;
          const merged = { ...prev, ...updatedTravel, waypoints: prev.waypoints };
          activeTravelRef.current = merged;
          return merged;
        });

        setLastError(null);
        return result;
      }

      setLastError(
        ApiError.fromResult(result, "Failed to discard tracking")
      );
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      if (mountedRef.current) {
        setLastError(
          ApiError.fromException(error, "Failed to discard tracking")
        );
      }
      return result;
    } finally {
      if (mountedRef.current) {
        setIsDiscardingTracking(false);
      }
    }
  }, [trackingActions]); // Stable: reads activeTravel from ref

  // ============================================
  // Manual state management
  // ============================================

  const setActiveTravel = useCallback((travel: Travel | null) => {
    setActiveTravelState(travel);
    // If setting externally, we don't know if waypoints are included
    loadedWithWaypointsTravelIdRef.current = null;
  }, []);

  const clearActiveTravel = useCallback(() => {
    setActiveTravelState(null);
    loadedWithWaypointsTravelIdRef.current = null;
  }, []);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    // Force full reload including waypoints by clearing the cache
    loadedWithWaypointsTravelIdRef.current = null;
    await loadActiveTravel(false);
  }, [loadActiveTravel]);

  // ============================================
  // Visibility change handler
  // ============================================

  // Stable refs for effect dependencies that should not cause re-subscription
  const refreshIntervalMsRef = useRef(refreshIntervalMs);
  refreshIntervalMsRef.current = refreshIntervalMs;

  useEffect(() => {
    if (!pauseWhenHidden) return;

    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsTabVisible(isVisible);

      // Refresh immediately when tab becomes visible (if enabled and enough time has passed)
      if (
        isVisible &&
        refreshOnVisible &&
        isAuthenticatedRef.current &&
        hasLoadedRef.current
      ) {
        const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
        // Only refresh if at least half the interval has passed
        const minTimeBetweenRefreshes =
          refreshIntervalMsRef.current > 0
            ? refreshIntervalMsRef.current / 2
            : 30000;

        if (timeSinceLastRefresh >= minTimeBetweenRefreshes) {
          loadActiveTravel(true); // Background refresh
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [pauseWhenHidden, refreshOnVisible, loadActiveTravel]);

  // ============================================
  // Auto-refresh interval
  // ============================================

  useEffect(() => {
    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Don't set up interval if:
    // - Refresh is disabled (interval <= 0)
    // - User is not authenticated
    // - Initial load hasn't completed
    // - Tab is hidden and pauseWhenHidden is enabled
    if (
      refreshIntervalMs <= 0 ||
      !isAuthenticated ||
      !hasLoaded ||
      (pauseWhenHidden && !isTabVisible)
    ) {
      return;
    }

    // Set up new interval
    intervalRef.current = setInterval(() => {
      loadActiveTravel(true); // Background refresh (no loading indicator)
    }, refreshIntervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    refreshIntervalMs,
    isAuthenticated,
    hasLoaded,
    pauseWhenHidden,
    isTabVisible,
    loadActiveTravel,
  ]);

  // ============================================
  // Auto-load on mount and auth change
  // ============================================

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasLoaded) {
      loadActiveTravel(false);
    }

    // Clear state when user logs out
    if (!isAuthenticated) {
      setActiveTravelState(null);
      setHasLoaded(false);
      setLastError(null);
      lastRefreshRef.current = 0;
      loadedWithWaypointsTravelIdRef.current = null;
      // Invalidate any in-flight load
      loadCallIdRef.current++;
    }
  }, [autoLoad, isAuthenticated, hasLoaded, loadActiveTravel]);

  // ============================================
  // Context value
  // ============================================

  const contextValue = useMemo<ActiveTravelContextValue>(
    () => ({
      // State
      activeTravel,
      loading,

      // Computed
      hasActiveTravel,
      activeTravelCarId,
      latestWaypoint,

      // Computed tracking state
      isTracking,
      isTrackingPaused,
      hasTracking,
      trackingStatus,
      currentSegmentId,
      gpsDistance,

      // Operations
      startTravel,
      completeTravel,
      addWaypoint,

      pauseTracking,
      resumeTracking,
      discardTracking,

      // Operation states
      isStarting,
      isCompleting,
      isAddingWaypoint,

      isPausingTracking,
      isResumingTracking,
      isDiscardingTracking,

      // Client-side tracking session
      trackingSession,

      // Refresh
      refresh,

      // Manual state management
      setActiveTravel,
      clearActiveTravel,

      // Error handling
      lastError,
      clearError,

      // Refresh interval info
      refreshIntervalMs,
      isRefreshPaused,
    }),
    [
      activeTravel,
      loading,
      hasActiveTravel,
      activeTravelCarId,
      latestWaypoint,
      isTracking,
      isTrackingPaused,
      hasTracking,
      trackingStatus,
      currentSegmentId,
      gpsDistance,
      startTravel,
      completeTravel,
      addWaypoint,
      pauseTracking,
      resumeTracking,
      discardTracking,
      isStarting,
      isCompleting,
      isAddingWaypoint,
      isPausingTracking,
      isResumingTracking,
      isDiscardingTracking,
      trackingSession,
      refresh,
      setActiveTravel,
      clearActiveTravel,
      lastError,
      clearError,
      refreshIntervalMs,
      isRefreshPaused,
    ]
  );

  return (
    <ActiveTravelContext.Provider value={contextValue}>
      {children}
    </ActiveTravelContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Hook to access active travel state from context.
 * Must be used within ActiveTravelProvider.
 *
 * @example
 * ```tsx
 * const {
 *   activeTravel,
 *   hasActiveTravel,
 *   latestWaypoint,
 *   startTravel,
 *   completeTravel,
 *   addWaypoint,
 *   // Tracking
 *   isTracking,
 *   isTrackingPaused,
 *   pauseTracking,
 *   resumeTracking,
 *   discardTracking,
 *   gpsDistance,
 * } = useActiveTravelContext();
 *
 * // Start a travel with live tracking enabled
 * const result = await startTravel({ carId: '123', enableTracking: true, ... });
 *
 * // Pause/resume tracking
 * await pauseTracking();
 * await resumeTracking();
 *
 * // Complete the travel — tracking auto-completes on backend
 * const result = await completeTravel(activeTravel.id, { lastRecord: { ... } });
 *
 * // Discard tracking data without ending the travel
 * await discardTracking();
 * ```
 */
export const useActiveTravelContext = (): ActiveTravelContextValue => {
  const context = useContext(ActiveTravelContext);

  if (context === undefined) {
    throw new Error(
      "useActiveTravelContext must be used within an ActiveTravelProvider"
    );
  }

  return context;
};